import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { parseAppLocation, researchPath } from '../routing/appRoutes';
import {
  Box,
  Typography,
  List,
  ListItemButton,
  ListItemText,
  Divider,
  CircularProgress,
  Chip,
  IconButton,
  Button,
  Stack,
  Snackbar,
  Alert,
  Drawer,
  Fab,
  TextField,
  ListItem,
  ListItemSecondaryAction,
  Checkbox,
  MenuItem,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import PermMediaIcon from '@mui/icons-material/PermMedia';
import ImageIcon from '@mui/icons-material/Image';
import LinkIcon from '@mui/icons-material/Link';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { NotebooksContext, FrontendNotebook } from '../contexts/NotebooksContext';
import axiosInstance from '../api/axiosInstance';
import type { NewsCluster, WikiConcept } from '../types/types';
import { LibraryDrawerTitleChrome } from '../components/draftCreator/LibraryDrawerTitleChrome';
import { LibraryLinkedContentRail } from '../components/draftCreator/LibraryLinkedContentRail';
import LibraryClusterInsightsDrawer from '../components/draftCreator/LibraryClusterInsightsDrawer';

type StagedKind = 'pdf' | 'media' | 'image' | 'url';

interface StagedFile {
  id: string;
  kind: StagedKind;
  name: string;
  size: number;
  /** Set when a URL responded successfully in the browser (CORS permitting). */
  urlLoaded?: boolean;
}

interface NotebookSource {
  id: string;
  documentId?: string;
  name: string;
  kind: string;
  status?: string;
  markdown?: string;
  pages: number;
  error?: string;
  createdAt?: string;
  alreadyLinked?: boolean;
}

interface IngestedDocument {
  id: string;
  name: string;
  status: string;
  pages?: number;
  error?: string;
  createdAt?: string;
}

function sourceStatusLabel(status?: string): string {
  if (status === 'pending') return 'Queued';
  if (status === 'extracting') return 'Reading';
  if (status === 'failed') return 'Failed';
  return 'Ready';
}

async function probeUrlReachable(url: string): Promise<boolean> {
  const controller = new AbortController();
  const timeoutId = window.setTimeout(() => controller.abort(), 12000);
  try {
    const res = await fetch(url, {
      method: 'GET',
      mode: 'cors',
      credentials: 'omit',
      signal: controller.signal,
      redirect: 'follow',
    });
    if (res.ok) return true;
  } catch {
    /* try HEAD below */
  } finally {
    window.clearTimeout(timeoutId);
  }
  try {
    const res = await fetch(url, { method: 'HEAD', mode: 'cors', credentials: 'omit', redirect: 'follow' });
    return res.ok;
  } catch {
    return false;
  }
}

function stagedKindIcon(kind: StagedKind) {
  const sx = { fontSize: 20, color: '#a1a1aa', flexShrink: 0 } as const;
  switch (kind) {
    case 'pdf':
      return <PictureAsPdfIcon sx={sx} titleAccess="PDF" />;
    case 'media':
      return <PermMediaIcon sx={sx} titleAccess="Video or audio" />;
    case 'image':
      return <ImageIcon sx={sx} titleAccess="Image" />;
    default:
      return <LinkIcon sx={sx} titleAccess="URL" />;
  }
}

const LayoutResearch: React.FC = () => {
  const {
    notebooks,
    isLoading,
    hasLoaded: notebooksHaveLoaded,
    addNotebook,
    updateNotebook,
    removeClustersFromNotebook,
    isClusterContentGenerating,
  } = useContext(NotebooksContext);
  const [selected, setSelected] = useState<FrontendNotebook | null>(null);
  const [selectedClusterForInsights, setSelectedClusterForInsights] = useState<NewsCluster | null>(null);
  const [stagedFiles, setStagedFiles] = useState<StagedFile[]>([]);
  const [urlInput, setUrlInput] = useState('');
  const [dropActive, setDropActive] = useState(false);
  const [toast, setToast] = useState<{ open: boolean; message: string; severity: 'success' | 'info' }>({
    open: false,
    message: '',
    severity: 'info',
  });
  const [urlBusy, setUrlBusy] = useState(false);
  const [creatingNotebook, setCreatingNotebook] = useState(false);
  const [sources, setSources] = useState<NotebookSource[]>([]);
  const [documents, setDocuments] = useState<IngestedDocument[]>([]);
  const [linkDocumentId, setLinkDocumentId] = useState('');
  const [pdfBusy, setPdfBusy] = useState(false);
  const pdfInputRef = React.useRef<HTMLInputElement | null>(null);
  const sourcesWaiting = sources.some((source) => source.status === 'pending' || source.status === 'extracting');

  const activeNotebooks = useMemo(
    () => notebooks.filter((n) => n.status === 'active').sort((a, b) => b.updated_at.localeCompare(a.updated_at)),
    [notebooks]
  );

  const activeNotebookChoices = useMemo(() => notebooks.filter((n) => n.status === 'active'), [notebooks]);

  const linkableDocuments = useMemo(() => {
    const linked = new Set(sources.map((source) => source.documentId).filter(Boolean));
    return documents.filter((doc) => !linked.has(doc.id));
  }, [documents, sources]);

  const clearSelection = useCallback(() => {
    setSelected(null);
    setSelectedClusterForInsights(null);
    setUrlInput('');
  }, []);

  useEffect(() => {
    if (!selected?.id) return;
    const fresh = notebooks.find((n) => n.id === selected.id && n.status === 'active');
    if (!fresh) {
      clearSelection();
      return;
    }
    setSelected(fresh);
  }, [notebooks, selected?.id, clearSelection]);

  useEffect(() => {
    setStagedFiles([]);
    setUrlInput('');
  }, [selected?.id]);

  const loadNotebookDocuments = useCallback(async (notebookId: string) => {
    const [sourceRes, documentRes] = await Promise.all([
      axiosInstance.get(`/api/notebooks/${notebookId}/sources`),
      axiosInstance.get('/api/notebooks/documents'),
    ]);
    setSources((sourceRes.data?.sources as NotebookSource[]) || []);
    setDocuments((documentRes.data?.documents as IngestedDocument[]) || []);
  }, []);

  useEffect(() => {
    if (!selected?.id) {
      setSources([]);
      setDocuments([]);
      setLinkDocumentId('');
      return;
    }
    let cancelled = false;
    void loadNotebookDocuments(selected.id).catch(() => {
      if (!cancelled) {
        setSources([]);
        setDocuments([]);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [selected?.id, loadNotebookDocuments]);

  useEffect(() => {
    if (!selected?.id || !sourcesWaiting) return;
    const timer = window.setInterval(() => {
      void loadNotebookDocuments(selected.id).catch(() => undefined);
    }, 2000);
    return () => window.clearInterval(timer);
  }, [selected?.id, sourcesWaiting, loadNotebookDocuments]);

  const uploadPdf = async (file: File) => {
    if (!selected?.id) return;
    setPdfBusy(true);
    try {
      const body = new FormData();
      body.append('file', file);
      const res = await axiosInstance.post(`/api/notebooks/${selected.id}/sources`, body);
      const created = res.data as NotebookSource;
      setSources((prev) => [created, ...prev.filter((item) => item.id !== created.id)]);
      if (created.alreadyLinked) {
        showToast('This PDF is already in the notebook.', 'info');
      } else if (created.status === 'ready') {
        showToast('Linked the existing document.', 'success');
      } else {
        showToast(`Queued ${file.name} for reading.`, 'success');
      }
      void loadNotebookDocuments(selected.id).catch(() => undefined);
    } catch (err: unknown) {
      const data = (err as { response?: { data?: { error?: string } } })?.response?.data;
      showToast(data?.error || 'Could not read this PDF.', 'info');
    } finally {
      setPdfBusy(false);
    }
  };

  const removeSource = async (id: string) => {
    if (!selected?.id) return;
    await axiosInstance.delete(`/api/notebooks/${selected.id}/sources/${id}`);
    setSources((prev) => prev.filter((item) => item.id !== id));
  };

  const linkExistingDocument = async () => {
    if (!selected?.id || !linkDocumentId) return;
    try {
      const res = await axiosInstance.post(`/api/notebooks/${selected.id}/sources/link`, { documentId: linkDocumentId });
      const created = res.data as NotebookSource;
      setSources((prev) => [created, ...prev.filter((item) => item.id !== created.id)]);
      setLinkDocumentId('');
      showToast(created.alreadyLinked ? 'This document is already in the notebook.' : 'Linked the document.', created.alreadyLinked ? 'info' : 'success');
    } catch (err: unknown) {
      const data = (err as { response?: { data?: { error?: string } } })?.response?.data;
      showToast(data?.error || 'Could not link this document.', 'info');
    }
  };

  // ---- URL sync: /research/:notebookId (see routing/appRoutes.ts) ----
  const navigate = useNavigate();
  const location = useLocation();
  const routeLocation = parseAppLocation(location.pathname);
  const routeNotebookId = routeLocation?.view === 'research' ? routeLocation.itemId : undefined;
  /** Notebook id from a deep link that hasn't been selected yet (waiting for notebooks to load). */
  const pendingNotebookIdRef = useRef<string | null>(null);

  // State → URL: selecting / closing a notebook updates the address bar.
  const prevSelectedIdRef = useRef(selected?.id ?? null);
  useEffect(() => {
    const id = selected?.id ?? null;
    if (prevSelectedIdRef.current === id) return;
    prevSelectedIdRef.current = id;
    if (pendingNotebookIdRef.current) return;
    const path = researchPath(id ?? undefined);
    if (location.pathname !== path) navigate(path);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- react only to selection changes
  }, [selected?.id]);

  // URL → state: deep links and back/forward.
  const prevPathnameRef = useRef<string | null>(null);
  useEffect(() => {
    if (prevPathnameRef.current !== location.pathname) {
      prevPathnameRef.current = location.pathname;
      pendingNotebookIdRef.current = routeNotebookId ?? null;
      if (!routeNotebookId && selected) clearSelection();
    }
    const id = pendingNotebookIdRef.current;
    if (!id) return;
    if (selected?.id === id) {
      pendingNotebookIdRef.current = null;
      return;
    }
    const nb = notebooks.find((n) => n.id === id && n.status === 'active');
    if (nb) {
      pendingNotebookIdRef.current = null;
      setSelected(nb);
    } else if (notebooksHaveLoaded && !isLoading) {
      pendingNotebookIdRef.current = null;
      setToast({
        open: true,
        message: "That notebook wasn't found. It may belong to another analytic profile, or it was archived or deleted.",
        severity: 'info',
      });
      navigate(researchPath(), { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- selected/clearSelection read at path change only
  }, [location.pathname, notebooks, notebooksHaveLoaded, isLoading]);

  const selectNotebook = (nb: FrontendNotebook) => {
    setSelected(nb);
  };

  const showToast = (message: string, severity: 'success' | 'info' = 'info') => {
    setToast({ open: true, message, severity });
  };

  const handleCreateNotebook = async () => {
    if (creatingNotebook) return;
    setCreatingNotebook(true);
    try {
      const created = await addNotebook({
        name: 'Untitled notebook',
        description: '',
        clusters: [],
        status: 'active',
        visibility: 'private',
        allowed_profiles: [],
        linked_notebook_ids: [],
      });
      if (created) {
        setSelected({
          ...created,
          linked_notebook_ids: created.linked_notebook_ids ?? [],
          cluster_ids: created.cluster_ids ?? [],
        });
      } else {
        showToast('Could not create notebook.', 'info');
      }
    } finally {
      setCreatingNotebook(false);
    }
  };

  const stageDroppedFiles = (files: File[]) => {
    if (!selected || files.length === 0) return;
    const next: StagedFile[] = [];
    files.forEach((file, i) => {
      const mime = (file.type || '').toLowerCase();
      const name = file.name.toLowerCase();
      if (mime === 'application/pdf' || name.endsWith('.pdf')) {
        void uploadPdf(file);
        return;
      }
      let kind: Exclude<StagedKind, 'url'> = 'media';
      if (mime.startsWith('image/') || /\.(png|jpe?g|gif|webp|svg)$/i.test(name)) kind = 'image';
      next.push({
        id: `${Date.now()}-drop-${i}-${file.name}`,
        kind,
        name: file.name,
        size: file.size,
      });
    });
    if (next.length === 0) return;
    setStagedFiles((prev) => [...prev, ...next]);
    showToast(`Kept ${next.length} file(s) with this notebook. PDFs are queued for reading.`, 'success');
  };

  const addUrlToQueue = async () => {
    const raw = urlInput.trim();
    if (!selected || !raw || urlBusy) return;
    let parsed: URL;
    try {
      parsed = new URL(raw);
    } catch {
      showToast('Enter a valid URL (including https://).', 'info');
      return;
    }
    if (!parsed.protocol.startsWith('http')) {
      showToast('Only http(s) URLs are supported.', 'info');
      return;
    }
    setUrlBusy(true);
    try {
      const ok = await probeUrlReachable(parsed.href);
      if (!ok) {
        showToast('Could not load this URL from the browser (network or CORS).', 'info');
        return;
      }
      setStagedFiles((prev) => [
        ...prev,
        { id: `${Date.now()}-url`, kind: 'url' as const, name: parsed.href, size: 0, urlLoaded: true },
      ]);
      setUrlInput('');
      showToast('URL added to linked items.', 'success');
    } finally {
      setUrlBusy(false);
    }
  };

  const removeStaged = (id: string) => {
    setStagedFiles((prev) => prev.filter((x) => x.id !== id));
  };

  const formatBytes = (n: number) => {
    if (n <= 0) return '—';
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
    return `${(n / (1024 * 1024)).toFixed(1)} MB`;
  };

  const researchDrawerUsesWidePaper = true;

  const handleLinkedNotebookIdsChange = useCallback(
    async (next: string[]) => {
      if (!selected) return;
      await updateNotebook(selected.id, { linked_notebook_ids: next });
    },
    [selected, updateNotebook]
  );

  const handleOpenClusterDetails = useCallback((event: React.MouseEvent, cluster: NewsCluster) => {
    event.preventDefault();
    event.stopPropagation();
    setSelectedClusterForInsights(cluster);
  }, []);

  const handleRemoveCluster = useCallback(
    async (clusterTitle: string) => {
      if (!selected) return false;
      const cl = selected.clusters?.find((c) => c.title === clusterTitle);
      const ref = ((cl?.cluster_id || cl?.title || clusterTitle) as string).trim();
      if (!ref) return false;
      return removeClustersFromNotebook(selected.id, [ref]);
    },
    [selected, removeClustersFromNotebook]
  );

  const handleWikiEntitiesChange = useCallback(
    async (next: WikiConcept[]) => {
      if (!selected) return;
      const base = selected.draft ?? { title: selected.name || '', content: '' };
      await updateNotebook(selected.id, {
        draft: {
          ...base,
          linked_wiki_entities: next,
        },
      });
    },
    [selected, updateNotebook]
  );

  const clusterGenerating = Boolean(
    selectedClusterForInsights &&
      isClusterContentGenerating(selectedClusterForInsights.title, selected?.id)
  );

  return (
    <Box
      sx={{
        bgcolor: '#0a0a0a',
        minHeight: 'calc(100vh - 80px)',
        display: 'flex',
        flexDirection: 'column',
        px: { xs: 2, md: 3 },
        pt: 2,
        pb: 3,
      }}
    >
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          width: '100%',
          border: '1px solid #27272a',
          borderRadius: 1,
          bgcolor: '#09090b',
          overflow: 'auto',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress size={36} sx={{ color: '#60a5fa' }} />
          </Box>
        ) : activeNotebooks.length === 0 ? (
          <Typography sx={{ color: '#a1a1aa', p: 3 }}>No active notebooks yet.</Typography>
        ) : (
          <List disablePadding>
            {activeNotebooks.map((nb) => (
              <React.Fragment key={nb.id}>
                <ListItemButton
                  selected={selected?.id === nb.id}
                  onClick={() => selectNotebook(nb)}
                  sx={{ py: 1.5, px: 2, alignItems: 'flex-start' }}
                >
                  <ListItemText
                    primary={nb.name || 'Untitled notebook'}
                    secondary={nb.description || 'No description'}
                    primaryTypographyProps={{ sx: { color: '#fafafa', fontWeight: 600 } }}
                    secondaryTypographyProps={{ sx: { color: '#71717a', mt: 0.5 } }}
                  />
                  <Chip
                    size="small"
                    label={`${(nb.clusters || []).length} clusters`}
                    sx={{ ml: 1, flexShrink: 0, bgcolor: '#27272a', color: '#e4e4e7' }}
                  />
                </ListItemButton>
                <Divider component="li" sx={{ borderColor: '#27272a' }} />
              </React.Fragment>
            ))}
          </List>
        )}
      </Box>

      <Drawer
        anchor="right"
        open={Boolean(selected)}
        onClose={clearSelection}
        ModalProps={{
          keepMounted: false,
          disableEnforceFocus: true,
          disableAutoFocus: true,
        }}
        PaperProps={{
          sx: {
            bgcolor: '#0a0a0a',
            borderLeft: '1px solid #27272a',
            width: { xs: '100%', sm: researchDrawerUsesWidePaper ? '70vw' : '50vw' },
            maxWidth: { xs: '100%', sm: researchDrawerUsesWidePaper ? 1680 : 960 },
            display: 'flex',
            flexDirection: 'column',
          },
        }}
      >
        {selected ? (
          <>
            <Box
              sx={{
                bgcolor: '#0a0a0a',
                borderBottom: '1px solid #27272a',
                px: 2,
                py: 1.25,
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'space-between',
                gap: 1,
                flexShrink: 0,
              }}
            >
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <LibraryDrawerTitleChrome
                  kindIcon={<MenuBookIcon sx={{ fontSize: 24 }} />}
                  displayTitle={selected.name || 'Untitled notebook'}
                  isEditingTitle={false}
                  onRequestEditTitle={() => {}}
                  onTitleChange={() => {}}
                  titleDisabled
                  showTitleSuggestions={false}
                  accessBar={null}
                />
              </Box>
              <Box display="flex" alignItems="center" flexShrink={0}>
                <IconButton onClick={clearSelection} sx={{ color: '#a1a1aa', '&:hover': { color: '#f87171' } }} aria-label="Close">
                  <CloseIcon />
                </IconButton>
              </Box>
            </Box>

            <Box
              sx={{
                bgcolor: '#0a0a0a',
                px: 2,
                flex: 1,
                minHeight: 0,
                overflow: 'auto',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <Box sx={{ py: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box>
                  <Typography variant="subtitle2" sx={{ color: '#fafafa', fontWeight: 700, mb: 1 }}>
                    New content
                  </Typography>
                  <Box
                    onDragEnter={(e) => {
                      e.preventDefault();
                      setDropActive(true);
                    }}
                    onDragOver={(e) => {
                      e.preventDefault();
                      e.dataTransfer.dropEffect = 'copy';
                    }}
                    onDragLeave={() => setDropActive(false)}
                    onDrop={(e) => {
                      e.preventDefault();
                      setDropActive(false);
                      const files = Array.from(e.dataTransfer.files || []);
                      stageDroppedFiles(files);
                    }}
                    sx={{
                      border: `2px dashed ${dropActive ? '#60a5fa' : '#3f3f46'}`,
                      borderRadius: 1,
                      px: 2,
                      py: 3,
                      textAlign: 'center',
                      bgcolor: dropActive ? 'rgba(96,165,250,0.06)' : '#09090b',
                      transition: 'border-color 0.15s, background 0.15s',
                    }}
                  >
                    <AttachFileIcon sx={{ fontSize: 32, color: '#71717a', mb: 0.5 }} />
                    <Typography variant="body2" sx={{ color: '#a1a1aa' }}>
                      Drop a PDF to queue it for reading. The markdown is stored once and can be linked from any notebook. Images, video, and audio stay with this notebook.
                    </Typography>
                    <Button
                      size="small"
                      variant="outlined"
                      disabled={pdfBusy || !selected}
                      onClick={() => pdfInputRef.current?.click()}
                      sx={{ mt: 1.5, color: '#fafafa', borderColor: '#3f3f46', textTransform: 'none' }}
                    >
                      {pdfBusy ? 'Queuing…' : 'Load PDF'}
                    </Button>
                    <input
                      ref={pdfInputRef}
                      type="file"
                      accept="application/pdf,.pdf"
                      hidden
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        e.target.value = '';
                        if (file) void uploadPdf(file);
                      }}
                    />
                  </Box>

                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mt: 1.5 }} alignItems={{ sm: 'center' }}>
                    <TextField
                      size="small"
                      fullWidth
                      placeholder="https://…"
                      value={urlInput}
                      onChange={(e) => setUrlInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') void addUrlToQueue();
                      }}
                      disabled={urlBusy}
                      sx={fieldSx}
                    />
                    <Button
                      variant="outlined"
                      startIcon={urlBusy ? undefined : <LinkIcon />}
                      onClick={() => void addUrlToQueue()}
                      disabled={urlBusy}
                      sx={{ ...pickBtnSx, flexShrink: 0, minWidth: 120 }}
                    >
                      {urlBusy ? <CircularProgress size={20} sx={{ color: '#e4e4e7' }} /> : 'Add URL'}
                    </Button>
                  </Stack>
                </Box>

                <Divider sx={{ borderColor: '#27272a' }} />

                <Box sx={{ minHeight: 280, display: 'flex', flexDirection: 'column' }}>
                  <Typography variant="subtitle2" sx={{ color: '#fafafa', fontWeight: 700, mb: 0.5 }}>
                    Link content
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#71717a', display: 'block', mb: 1 }}>
                    Search library drafts and link notebooks—same flow as article draft editing. Clusters on this
                    notebook appear here with an eye to open details.
                  </Typography>
                  <Box sx={{ flex: 1, minHeight: 260, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                    <LibraryLinkedContentRail
                      mode="notebook"
                      subjectId={selected.id}
                      readOnly={false}
                      linkedNotebookIds={selected.linked_notebook_ids ?? []}
                      onLinkedNotebookIdsChange={handleLinkedNotebookIdsChange}
                      allNotebooks={notebooks}
                      activeNotebooks={activeNotebookChoices}
                      linkedClusters={selected.clusters ?? []}
                      onOpenClusterDetails={handleOpenClusterDetails}
                      onRemoveCluster={handleRemoveCluster}
                      draftLinkedWikiEntities={selected.draft?.linked_wiki_entities ?? []}
                      onDraftLinkedWikiEntitiesChange={selected.draft ? handleWikiEntitiesChange : undefined}
                    />
                  </Box>
                </Box>

                <Box>
                  <Typography variant="subtitle2" sx={{ color: '#fafafa', fontWeight: 700, mb: 0.5 }}>
                    Ingested documents
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#71717a', display: 'block', mb: 1 }}>
                    PDFs already read for this company. Link one to use its markdown in this notebook.
                  </Typography>
                  {linkableDocuments.length > 0 ? (
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
                      <TextField
                        select
                        size="small"
                        fullWidth
                        value={linkDocumentId}
                        onChange={(e) => setLinkDocumentId(e.target.value)}
                        SelectProps={{ displayEmpty: true }}
                        sx={fieldSx}
                      >
                        <MenuItem value="">Choose a document</MenuItem>
                        {linkableDocuments.map((doc) => (
                          <MenuItem key={doc.id} value={doc.id}>
                            {doc.name}
                            {doc.status && doc.status !== 'ready' ? ` (${sourceStatusLabel(doc.status)})` : ''}
                          </MenuItem>
                        ))}
                      </TextField>
                      <Button
                        variant="outlined"
                        disabled={!linkDocumentId}
                        onClick={() => void linkExistingDocument()}
                        sx={{ ...pickBtnSx, flexShrink: 0, minWidth: 120 }}
                      >
                        Link
                      </Button>
                    </Stack>
                  ) : (
                    <Typography variant="body2" sx={{ color: '#71717a' }}>
                      No other ingested PDFs yet.
                    </Typography>
                  )}
                </Box>

                {sources.length > 0 ? (
                  <Box>
                    <Typography variant="subtitle2" sx={{ color: '#fafafa', fontWeight: 700, mb: 1 }}>
                      PDFs in this notebook
                    </Typography>
                    <List dense disablePadding>
                      {sources.map((source) => (
                        <ListItem
                          key={source.id}
                          sx={{
                            border: '1px solid #27272a',
                            borderRadius: 1,
                            mb: 0.75,
                            py: 1,
                            bgcolor: '#09090b',
                            display: 'block',
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <PictureAsPdfIcon sx={{ fontSize: 20, color: '#a1a1aa' }} />
                            <Typography variant="body2" sx={{ color: '#fafafa', flex: 1 }} noWrap>
                              {source.name}
                            </Typography>
                            <Chip
                              size="small"
                              label={sourceStatusLabel(source.status)}
                              sx={{
                                height: 22,
                                color: source.status === 'failed' ? '#fecaca' : '#e4e4e7',
                                bgcolor: source.status === 'failed' ? 'rgba(248,113,113,0.15)' : '#27272a',
                              }}
                            />
                            {source.pages > 0 ? (
                              <Typography variant="caption" sx={{ color: '#71717a' }}>
                                {source.pages}p
                              </Typography>
                            ) : null}
                            <IconButton size="small" aria-label="Remove PDF from notebook" onClick={() => void removeSource(source.id)} sx={{ color: '#a1a1aa' }}>
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          </Box>
                          {source.status === 'failed' ? (
                            <Typography variant="body2" sx={{ color: '#fca5a5', mt: 1 }}>
                              {source.error || 'Reading this PDF failed.'}
                            </Typography>
                          ) : source.markdown ? (
                            <Typography
                              variant="body2"
                              sx={{ color: '#d4d4d8', mt: 1, whiteSpace: 'pre-wrap', maxHeight: 160, overflow: 'auto', lineHeight: 1.45 }}
                            >
                              {source.markdown}
                            </Typography>
                          ) : (
                            <Typography variant="body2" sx={{ color: '#71717a', mt: 1 }}>
                              Markdown will appear when reading finishes.
                            </Typography>
                          )}
                        </ListItem>
                      ))}
                    </List>
                  </Box>
                ) : null}

                {stagedFiles.length > 0 ? (
                  <>
                    <Divider sx={{ borderColor: '#27272a', mt: 1 }} />
                    <Box>
                      <Typography variant="subtitle2" sx={{ color: '#fafafa', fontWeight: 700, mb: 1 }}>
                        Linked items
                      </Typography>
                      <List dense disablePadding>
                        {stagedFiles.map((f) => (
                          <ListItem
                            key={f.id}
                            sx={{
                              border: '1px solid #27272a',
                              borderRadius: 1,
                              mb: 0.75,
                              py: 0.75,
                              pr: f.kind === 'url' && f.urlLoaded ? '96px' : '48px',
                              bgcolor: '#09090b',
                              boxSizing: 'border-box',
                            }}
                          >
                            <ListItemText
                              primary={
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                                  {stagedKindIcon(f.kind)}
                                  <Typography variant="body2" sx={{ color: '#fafafa', fontSize: '0.85rem' }} noWrap>
                                    {f.name}
                                  </Typography>
                                </Box>
                              }
                              secondary={f.kind === 'url' ? 'URL' : formatBytes(f.size)}
                              secondaryTypographyProps={{ sx: { color: '#71717a', fontSize: '0.7rem' } }}
                            />
                            <ListItemSecondaryAction>
                              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                {f.kind === 'url' && f.urlLoaded ? (
                                  <Checkbox
                                    checked
                                    size="small"
                                    disabled
                                    disableRipple
                                    inputProps={{ 'aria-label': 'URL loaded' }}
                                    sx={{
                                      p: 0,
                                      mr: 0.5,
                                      color: '#22c55e',
                                      '&.Mui-disabled': { color: '#22c55e' },
                                    }}
                                  />
                                ) : null}
                                <IconButton size="small" aria-label="Remove" onClick={() => removeStaged(f.id)} sx={{ color: '#a1a1aa' }}>
                                  <DeleteOutlineIcon fontSize="small" />
                                </IconButton>
                              </Box>
                            </ListItemSecondaryAction>
                          </ListItem>
                        ))}
                      </List>
                    </Box>
                  </>
                ) : null}
              </Box>
            </Box>
          </>
        ) : null}
      </Drawer>

      <LibraryClusterInsightsDrawer
        open={Boolean(selectedClusterForInsights)}
        cluster={selectedClusterForInsights}
        onClose={() => setSelectedClusterForInsights(null)}
        showBackToArticle={Boolean(selected && selectedClusterForInsights)}
        onBackToArticle={() => setSelectedClusterForInsights(null)}
        draftBackLabel="Back to notebook"
        isGeneratingContent={clusterGenerating}
      />

      <Snackbar
        open={toast.open}
        autoHideDuration={5000}
        onClose={() => setToast((t) => ({ ...t, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setToast((t) => ({ ...t, open: false }))}
          severity={toast.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {toast.message}
        </Alert>
      </Snackbar>

      <Fab
        color="primary"
        aria-label="New notebook"
        disabled={creatingNotebook}
        onClick={() => void handleCreateNotebook()}
        sx={{
          position: 'fixed',
          right: 24,
          bottom: 24,
          zIndex: (t) => t.zIndex.drawer + 1,
          bgcolor: '#6366f1',
          '&:hover': { bgcolor: '#4f46e5' },
          '&.Mui-disabled': { bgcolor: '#3f3f46', color: '#a1a1aa' },
        }}
      >
        {creatingNotebook ? <CircularProgress size={26} sx={{ color: '#fafafa' }} /> : <AddIcon />}
      </Fab>
    </Box>
  );
};

const pickBtnSx = {
  borderColor: '#3f3f46',
  color: '#e4e4e7',
  '&:hover': { borderColor: '#60a5fa', bgcolor: 'rgba(96,165,250,0.08)' },
} as const;

const fieldSx = {
  '& .MuiOutlinedInput-root': {
    color: '#fafafa',
    fontSize: '0.8125rem',
    '& fieldset': { borderColor: '#3f3f46' },
    '&:hover fieldset': { borderColor: '#a1a1aa' },
    '&.Mui-focused fieldset': { borderColor: '#60a5fa' },
  },
} as const;

export default LayoutResearch;
